import type { EventType, WireBatch, WireEvent } from '@mado/schema';

/**
 * Viewer analytics.
 *
 * Two hard rules from CLAUDE.md, both product promises rather than preferences:
 *
 *  3. The embed sets NO cookies and NO persistent storage. Session identity is a
 *     per-page-load random id; unique-viewer counting happens server-side from a
 *     daily-rotating hash. This is what lets a customer embed Mado without
 *     touching their consent banner — competitors whose embeds set cookies cannot
 *     say that, and it is on the pricing page.
 *  4. Every interaction emits exactly one event, and events are never dropped.
 *     Delivery is best-effort from the client's side but never blocks the UI.
 *
 * The wire format is `WireBatch` in `@mado/schema` — imported as a type only,
 * so zod never enters this bundle. Every event carries `n`, a per-session
 * sequence number: it lets ingest dedupe on (session, n), which in turn lets
 * this file *retry* a failed batch without ever double counting.
 */

export type { EventType };
export type AnalyticsEvent = WireEvent;

export interface AnalyticsConfig {
  endpoint: string | null;
  demoId: string;
  version: number;
  /** Emitted alongside every batch so the server can attribute the embed. */
  referrer?: string;
  /**
   * Tracked-link label — `?for=acme` on the share URL, `data-for` on an
   * embed. Sent as `WireBatch.f` on every batch of this session. It comes
   * from the URL each load and is never stored here (rule 3); ingest
   * sanitizes it (packages/schema `sanitizeTrackedLabel`).
   */
  label?: string | null;
  /**
   * Hub key (`?hub=hk_…` on `/e/:slug`, set by in-app help when it opens a
   * demo). Sent as `WireBatch.h`, so the view is attributed to the hub. From
   * the URL each load, never stored here (rule 3). Validated by ingest.
   */
  hub?: string | null;
}

const FLUSH_MS = 2000;
const MAX_BATCH = 40;
/** `WireBatch.e` is capped at 200; anything beyond that goes in a second batch. */
const WIRE_MAX = 200;
const MAX_REFERRER = 2048;
/** `WireBatch.f`'s cap; anything longer is a 400 on the whole batch. */
const MAX_LABEL = 64;

export class Analytics {
  private queue: AnalyticsEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly session: string;
  private readonly t0 = performance.now();
  private seq = 0;
  private listeners: Array<(e: AnalyticsEvent) => void> = [];

  constructor(private cfg: AnalyticsConfig) {
    // Per page load. Deliberately NOT persisted — that would be a cookie by
    // another name.
    this.session =
      globalThis.crypto?.randomUUID?.() ??
      `s_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

    // A viewer closing the tab mid-demo is the common case, not the edge case.
    addEventListener('pagehide', () => this.flush(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush(true);
    });
  }

  /** Local subscriber, used by the dev harness and by editor previews. */
  on(fn: (e: AnalyticsEvent) => void): void {
    this.listeners.push(fn);
  }

  emit(type: EventType, stepId?: string, meta?: AnalyticsEvent['meta']): void {
    const e: AnalyticsEvent = { type, n: this.seq++, t: Math.round(performance.now() - this.t0) };
    if (stepId) e.stepId = stepId;
    if (meta) e.meta = meta;

    this.queue.push(e);
    for (const l of this.listeners) l(e);

    if (this.queue.length >= MAX_BATCH) this.flush();
    else if (!this.timer) this.timer = setTimeout(() => this.flush(), FLUSH_MS);
  }

  flush(final = false): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.queue.length || !this.cfg.endpoint) {
      if (final) this.queue = [];
      return;
    }

    const events = this.queue;
    this.queue = [];
    for (let i = 0; i < events.length; i += WIRE_MAX) this.send(events.slice(i, i + WIRE_MAX));
  }

  private send(e: AnalyticsEvent[]): void {
    const batch: WireBatch = {
      d: this.cfg.demoId,
      v: this.cfg.version,
      s: this.session,
      r: (this.cfg.referrer ?? document.referrer ?? '').slice(0, MAX_REFERRER),
      e,
    };
    if (this.cfg.label) batch.f = this.cfg.label.slice(0, MAX_LABEL);
    if (this.cfg.hub) batch.h = this.cfg.hub;
    const body = JSON.stringify(batch);
    const endpoint = this.cfg.endpoint!;

    // sendBeacon survives page unload, which fetch() does not reliably do.
    if (navigator.sendBeacon?.(endpoint, new Blob([body], { type: 'text/plain' }))) return;

    // Either no sendBeacon, or its queue is full. Fall back to fetch, and if
    // that fails, put the events back for the next flush — the sequence numbers
    // make the retry safe to count.
    void fetch(endpoint, { method: 'POST', body, keepalive: true })
      .then((r) => {
        if (!r.ok && r.status !== 400) this.requeue(e);
      })
      .catch(() => this.requeue(e));
  }

  private requeue(e: AnalyticsEvent[]): void {
    this.queue = e.concat(this.queue).slice(-WIRE_MAX);
    if (!this.timer) this.timer = setTimeout(() => this.flush(), FLUSH_MS);
  }
}
